import { auth, accessToken } from '$lib/stores/auth';
import { get } from 'svelte/store';

let isRefreshing = false;
type RefreshSubscriber = {
    onSuccess: (token: string) => void;
    onFailure: () => void;
};
let refreshSubscribers: RefreshSubscriber[] = [];

function subscribeToRefresh(onSuccess: (token: string) => void, onFailure: () => void) {
    refreshSubscribers.push({ onSuccess, onFailure });
}

function onRefreshed(token: string) {
    refreshSubscribers.forEach(subscriber => subscriber.onSuccess(token));
    refreshSubscribers = [];
}

function onRefreshFailed() {
    refreshSubscribers.forEach(subscriber => subscriber.onFailure());
    refreshSubscribers = [];
}

function withAuthorizationHeader(init: RequestInit | undefined, token: string): RequestInit {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);

    return {
        ...init,
        headers
    };
}

export async function fetchWithAuth(input: RequestInfo, init?: RequestInit, fetcher?: typeof fetch): Promise<Response> {
    const runFetch = fetcher ?? fetch;

    let token: string | null = null;
    try {
        token = await auth.getAccessToken();
    } catch {
        // Best effort only; fallback to request without bearer.
        token = get(accessToken);
    }
    
    if (token) {
        init = withAuthorizationHeader(init, token);
    }
    
    const response = await runFetch(input, init);

    if (response.status !== 401) {
        return response;
    }

    if (!isRefreshing) {
        isRefreshing = true;
        try {
            // Forsøk å fornye token.
            await auth.refreshAccessToken();

            // Hent nytt token fra store.
            const newToken = get(accessToken);

            // Retry once whenever refresh returned a token, even if token string is unchanged.
            // Some backends may return an equivalent token while still refreshing server-side session state.
            if (newToken) {
                onRefreshed(newToken);
                return runFetch(input, withAuthorizationHeader(init, newToken));
            }

            // Refresh ble kjørt, men ga ikke nytt token.
            onRefreshFailed();
            return response;
        } catch (error) {
            console.error('Failed to refresh token:', error);
            onRefreshFailed();
            return response;
        } finally {
            isRefreshing = false;
        }
    }

    // Hvis token allerede fornyes, vent på utfallet.
    return new Promise((resolve) => {
        subscribeToRefresh(
            token => resolve(runFetch(input, withAuthorizationHeader(init, token))),
            () => resolve(response)
        );
    });
}
